/**
 * Keeps the assets action bar's large filter-popover surface isolated so the
 * wrapper remains focused on arranging shared action-bar slots.
 */
import React, { useMemo, useState } from 'react';
import Column from '@amzn/meridian/column';
import Input from '@amzn/meridian/input';
import Select, { SelectOption } from '@amzn/meridian/select';
import { useTranslation } from 'src/arb-wrapper';
import type { AssetsActionBarProps } from 'src/components/assets/AssetsActionBar';
import { COUNTRY_OPTIONS } from 'src/components/common/CampaignFilterBar';
import { FEATURE_FLAGS, liveStatusRank } from 'src/constants';
import { isFeatureEnabled } from 'src/feature-flags';
import { BusinessLines, SportsBusinessLines } from 'src/store/constants';
import { useDistinctValuesControllerGetDistinctValuesQuery } from 'src/store/leela-api';
import { PUBLISHER_ALL } from 'src/utils/assetFilterUtils';

interface AssetSelectProps {
  testId: string;
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  disabled?: boolean;
  onChange: (value: string) => void;
}

const AssetSelect: React.FC<AssetSelectProps> = ({
  testId,
  label,
  value,
  options,
  disabled,
  onChange,
}) => (
  <Select
    data-testid={testId}
    disabled={disabled}
    label={label}
    placeholder={options[0]?.label}
    size="small"
    value={value}
    width={200}
    onChange={onChange}
  >
    {options.map((option) => (
      <SelectOption key={option.value || 'all'} label={option.label} value={option.value} />
    ))}
  </Select>
);

const sortLiveStatuses = (availableLiveStatuses: string[]): string[] =>
  [...availableLiveStatuses].sort(
    (first, second) => liveStatusRank(first) - liveStatusRank(second),
  );

export const AssetsActionBarFilters: React.FC<AssetsActionBarProps> = (props) => {
  const { t } = useTranslation();
  const [campaignSearchQuery, setCampaignSearchQuery] = useState('');
  const { data: availableLocales } = useDistinctValuesControllerGetDistinctValuesQuery({
    entityType: 'asset',
    field: 'locale',
    filters: { approvalStatus: props.approvalStatus },
  });

  const filteredCampaignOptions = useMemo(() => {
    const query = campaignSearchQuery.toLowerCase();
    return query === ''
      ? props.campaignOptions
      : props.campaignOptions.filter(({ id, name }) =>
        name.toLowerCase().includes(query) || id.toLowerCase().includes(query));
  }, [campaignSearchQuery, props.campaignOptions]);
  const businessLineOptions = useMemo(() => [
    ...Object.values(BusinessLines),
    ...(isFeatureEnabled(FEATURE_FLAGS.ENABLE_SPORTS_BUSINESS_LINES)
      ? Object.values(SportsBusinessLines)
      : []),
  ], []);
  const sortedLiveStatuses = useMemo(
    () => sortLiveStatuses(props.availableLiveStatuses),
    [props.availableLiveStatuses],
  );

  const allOption = { value: '', label: t('all') };
  const selectProps: AssetSelectProps[] = [
    {
      testId: 'asset-publisher-filter',
      label: t('publisher'),
      value: props.publisher,
      options: props.publishers.map((publisher) => ({
        value: publisher, label: publisher === PUBLISHER_ALL ? t('all') : publisher,
      })),
      onChange: props.onPublisherChange,
    },
    {
      testId: 'asset-business-line-filter',
      label: t('business_line'),
      value: props.businessLine,
      options: [allOption, ...businessLineOptions.map((value) => ({ value, label: value }))],
      onChange: props.onBusinessLineChange,
    },
    {
      testId: 'asset-channel-filter',
      label: t('channel_label'),
      value: props.channel,
      options: [allOption, { value: 'no-channel', label: t('no_channel') },
        ...props.availableChannels.map((value) => ({ value, label: value }))],
      disabled: props.isChannelsFetching,
      onChange: props.onChannelChange,
    },
    {
      testId: 'asset-locale-filter',
      label: t('form_locale'),
      value: props.locale,
      options: [allOption, ...(availableLocales ?? []).map(String).map((value) => ({ value, label: value }))],
      onChange: props.onLocaleChange,
    },
    {
      testId: 'asset-country-filter',
      label: t('country'),
      value: props.country,
      options: [allOption, ...COUNTRY_OPTIONS.map((value) => ({ value, label: value }))],
      onChange: props.onCountryChange,
    },
    {
      testId: 'asset-asset-type-filter',
      label: t('asset_type_label'),
      value: props.assetType,
      options: [allOption, { value: 'IMAGE', label: 'IMAGE' }, { value: 'VIDEO', label: 'VIDEO' }],
      onChange: props.onAssetTypeChange,
    },
    {
      testId: 'asset-size-filter',
      label: t('ad_size_label'),
      value: props.size,
      options: [allOption, ...props.availableSizes.map((value) => ({ value, label: value }))],
      disabled: props.isSizesFetching,
      onChange: props.onSizeChange,
    },
    {
      testId: 'asset-strategy-filter',
      label: t('product_strategy_label'),
      value: props.strategy,
      options: [allOption, ...props.strategyOptions],
      onChange: props.onStrategyChange,
    },
  ];

  return (
    <Column maxHeight={600} overflowY="auto" spacing="300" spacingInset="400">
      {selectProps.slice(0, 2).map((selectProp) => (
        <AssetSelect {...selectProp} key={selectProp.testId} />
      ))}
      <Select
        data-testid="asset-campaign-filter"
        label={t('campaign')}
        placeholder={t('search_campaigns')}
        popoverMaxHeight={300}
        searchQuery={campaignSearchQuery}
        size="small"
        value={props.campaigns}
        width={200}
        onChange={props.onCampaignsChange}
        onSearch={setCampaignSearchQuery}
      >
        {filteredCampaignOptions.length === 0
          ? <SelectOption disabled label={t('table_no_results')} value="" />
          : filteredCampaignOptions.map(({ id, name }) => (
            <SelectOption key={id} label={name} value={id} />
          ))}
      </Select>
      {selectProps.slice(2, 7).map((selectProp) => (
        <AssetSelect {...selectProp} key={selectProp.testId} />
      ))}
      {sortedLiveStatuses.length > 0 && (
        <AssetSelect
          label={t('live_status_label')}
          options={[allOption, ...sortedLiveStatuses.map((value) => ({ value, label: value }))]}
          testId="asset-live-status-filter"
          value={props.liveStatus}
          onChange={props.onLiveStatusChange}
        />
      )}
      <AssetSelect {...selectProps[7]} />
      {props.gti !== undefined && (
        <Input
          data-testid="asset-gti-filter"
          label={t('gti_label')}
          placeholder={t('filter_by_gti')}
          size="small"
          value={props.gti}
          width={250}
          onChange={(value: string) => { props.onGtiChange?.(value); }}
        />
      )}
    </Column>
  );
};
